import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { listCustomScriptsForObject } from '../../../api/CustomScriptApi';
import SchedulerRepeatFields from './SchedulerRepeatFields';
import SchedulerTimezoneSelect from './SchedulerTimezoneSelect';
import { DEFAULT_SCHEDULER_TIMEZONE } from './scheduledJobTimezones';
import {
  buildRepeatPayload,
  extractRepeatAtFromIso,
  isoToDatetimeLocal,
  mapRepeatTypeToFrequency,
  mergeRepeatAtIntoDatetimeLocal,
  MIN_MINUTELY_REPEAT_INTERVAL,
  repeatDayOfWeekToKeys,
  resolveEndDateFromRow,
  resolveStartDateFromRow,
} from './schedulerRepeatUi';
import {
  DEFAULT_SCHEDULED_JOB_ACTION,
  SCHEDULED_JOB_ACTIONS,
} from './scheduledJobActions';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const defaultForm = () => ({
  jobName: '',
  action: DEFAULT_SCHEDULED_JOB_ACTION,
  customScriptId: '',
  targetObject: '',
  recordId: '',
  start_date: '',
  end_date: '',
  repeat_frequency: 'once',
  repeat_interval: '1',
  repeat_on_day_keys: [],
  repeat_at: '',
  timezone: DEFAULT_SCHEDULER_TIMEZONE,
  is_active: true,
});

function formFromRow(row) {
  if (!row) return defaultForm();
  let c = row.config;
  if (typeof c === 'string') {
    try {
      c = JSON.parse(c);
    } catch {
      c = {};
    }
  }
  c = c || {};
  const objectName =
    row.object_name != null && String(row.object_name).trim() !== '' &&
    String(row.object_name).toLowerCase() !== 'scheduler'
      ? String(row.object_name).trim()
      : '';
  return {
    jobName: row.display_name || c.jobName || '',
    action: DEFAULT_SCHEDULED_JOB_ACTION,
    customScriptId: c.customScriptId || c.custom_script_id || '',
    targetObject: objectName,
    recordId: c.recordId || row.parent_id || '',
    start_date: resolveStartDateFromRow(row),
    end_date: resolveEndDateFromRow(row),
    repeat_frequency: mapRepeatTypeToFrequency(row.repeat_type),
    repeat_interval: row.repeat_interval != null ? String(row.repeat_interval) : '1',
    repeat_on_day_keys: repeatDayOfWeekToKeys(row.repeat_day_of_week),
    repeat_at: extractRepeatAtFromIso(row.next_run),
    timezone: row.timezone || DEFAULT_SCHEDULER_TIMEZONE,
    is_active: Boolean(row.is_active),
  };
}

/**
 * Create or edit a custom-script scheduler row from Setup → Scheduled Jobs.
 */
export default function ScheduledJobCreateModal({
  show,
  onHide,
  mode = 'create',
  editRow = null,
  onCreate,
  onSave,
  busy,
}) {
  const isEdit = mode === 'edit' && editRow != null;
  const [form, setForm] = useState(defaultForm);
  const [scripts, setScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [scriptError, setScriptError] = useState('');
  const [validationError, setValidationError] = useState('');

  const isCustomScript = form.action === 'custom_script';

  const onRepeatChange = (field, value) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (field === 'repeat_frequency' && value === 'once') {
        next.repeat_on_day_keys = [];
      }
      if (field === 'repeat_frequency' && value === 'minutely') {
        const cur = parseInt(next.repeat_interval, 10);
        if (!Number.isFinite(cur) || cur < MIN_MINUTELY_REPEAT_INTERVAL) {
          next.repeat_interval = String(MIN_MINUTELY_REPEAT_INTERVAL);
        }
      }
      if (field === 'repeat_at' && f.start_date) {
        next.start_date = mergeRepeatAtIntoDatetimeLocal(f.start_date, value);
      }
      if (field === 'start_date' && f.repeat_at) {
        next.start_date = mergeRepeatAtIntoDatetimeLocal(value, f.repeat_at);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!show) {
      setForm(defaultForm());
      setValidationError('');
      setScriptError('');
      return;
    }
    if (isEdit) {
      setForm(formFromRow(editRow));
    } else {
      setForm(defaultForm());
    }
    if (!isCustomScript) {
      setScripts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingScripts(true);
      setScriptError('');
      try {
        const target = String(form.targetObject || '').trim();
        const res = await listCustomScriptsForObject(target, {
          scriptTypes: ['backend'],
        });
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setScripts(res.data);
        } else {
          setScripts([]);
          setScriptError(res?.message || 'Could not load custom scripts.');
        }
      } catch (e) {
        if (!cancelled) {
          setScripts([]);
          setScriptError(e?.message || 'Failed to load custom scripts.');
        }
      } finally {
        if (!cancelled) setLoadingScripts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, form.targetObject, isCustomScript, isEdit, editRow]);

  const scriptById = useMemo(() => {
    const m = new Map();
    for (const r of scripts) {
      if (r.id) m.set(String(r.id), r);
    }
    return m;
  }, [scripts]);

  const selectedScript = form.customScriptId
    ? scriptById.get(String(form.customScriptId))
    : null;
  const scriptTargetObject =
    selectedScript?.target_object != null
      ? String(selectedScript.target_object).trim()
      : '';

  const handleSubmit = () => {
    setValidationError('');
    const jobName = String(form.jobName || '').trim();
    if (!jobName) {
      setValidationError('Job name is required.');
      return;
    }
    if (!form.action) {
      setValidationError('Select an action.');
      return;
    }
    if (!form.start_date) {
      setValidationError('Start Date is required.');
      return;
    }
    if (!form.end_date) {
      setValidationError('End Date is required.');
      return;
    }
    const startMs = new Date(form.start_date).getTime();
    const endMs = new Date(form.end_date).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
      setValidationError('Invalid Start Date or End Date.');
      return;
    }
    if (endMs <= startMs) {
      setValidationError('End Date must be after Start Date.');
      return;
    }

    if (form.action === 'custom_script') {
      const id = String(form.customScriptId || '').trim();
      if (!id || !UUID_RE.test(id)) {
        setValidationError('Select a backend custom script.');
        return;
      }
      const recordId = String(form.recordId || '').trim();
      if (scriptTargetObject && !recordId) {
        setValidationError(
          `This script is scoped to "${scriptTargetObject}"; provide a record id.`
        );
        return;
      }
      if (recordId && !UUID_RE.test(recordId)) {
        setValidationError('Record id must be a valid UUID.');
        return;
      }
    } else {
      setValidationError('This action is not available yet.');
      return;
    }

    const { payload: repeatPayload, error: repeatError } = buildRepeatPayload(form);
    if (repeatError) {
      setValidationError(repeatError);
      return;
    }

    let startLocal = form.start_date;
    if (form.repeat_at) {
      startLocal = mergeRepeatAtIntoDatetimeLocal(startLocal, form.repeat_at);
    }

    const startIso = new Date(startLocal).toISOString();
    const body = {
      jobName,
      action: form.action,
      customScriptId: String(form.customScriptId).trim(),
      start_date: startIso,
      next_run: startIso,
      end_date: new Date(form.end_date).toISOString(),
      timezone: form.timezone,
      is_active: form.is_active,
      ...repeatPayload,
    };
    if (scriptTargetObject || String(form.targetObject || '').trim()) {
      body.objectName = scriptTargetObject || String(form.targetObject).trim();
    }
    const recordId = String(form.recordId || '').trim();
    if (recordId) {
      body.recordId = recordId;
    }
    if (isEdit && editRow?.id) {
      onSave(editRow.id, body);
    } else {
      onCreate(body);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit scheduled job' : 'Create scheduled job'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {validationError ? (
          <div className="alert alert-danger py-2 small">{validationError}</div>
        ) : null}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Job name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={form.jobName}
              onChange={(e) => setForm((f) => ({ ...f, jobName: e.target.value }))}
              placeholder="e.g. Nightly account sync"
              required
            />
          </Form.Group>

          {isEdit ? (
            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Control plaintext readOnly value="Custom Script" />
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label>
                Action <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={form.action}
                onChange={(e) => {
                  const action = e.target.value;
                  setForm((f) => ({
                    ...f,
                    action,
                    customScriptId: action === 'custom_script' ? f.customScriptId : '',
                    targetObject: action === 'custom_script' ? f.targetObject : '',
                    recordId: action === 'custom_script' ? f.recordId : '',
                  }));
                }}
              >
                {SCHEDULED_JOB_ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </Form.Select>
              {SCHEDULED_JOB_ACTIONS.find((a) => a.value === form.action)?.description ? (
                <Form.Text className="text-muted">
                  {SCHEDULED_JOB_ACTIONS.find((a) => a.value === form.action).description}
                </Form.Text>
              ) : null}
            </Form.Group>
          )}

          {isCustomScript ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Target object (optional)</Form.Label>
                <Form.Control
                  value={form.targetObject}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      targetObject: e.target.value,
                      customScriptId: '',
                    }))
                  }
                  placeholder="e.g. account — filters script list; leave empty for universal scripts"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Backend custom script <span className="text-danger">*</span>
                </Form.Label>
                {loadingScripts && (
                  <div className="text-muted small mb-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading scripts…
                  </div>
                )}
                {scriptError && !loadingScripts ? (
                  <div className="text-danger small mb-2">{scriptError}</div>
                ) : null}
                <Form.Select
                  value={form.customScriptId}
                  onChange={(e) => setForm((f) => ({ ...f, customScriptId: e.target.value }))}
                  disabled={loadingScripts}
                  required
                >
                  <option value="">— Select backend script —</option>
                  {scripts.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.display_label || r.script_key || r.id}
                      {r.target_object ? ` · ${r.target_object}` : ' · (universal)'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {scriptTargetObject ? (
                <Form.Group className="mb-3">
                  <Form.Label>Record id ({scriptTargetObject})</Form.Label>
                  <Form.Control
                    value={form.recordId}
                    onChange={(e) => setForm((f) => ({ ...f, recordId: e.target.value }))}
                    placeholder="UUID of the record to pass to the script"
                  />
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Record id (optional)</Form.Label>
                  <Form.Control
                    value={form.recordId}
                    onChange={(e) => setForm((f) => ({ ...f, recordId: e.target.value }))}
                    placeholder="UUID — only if the script needs a record context"
                  />
                </Form.Group>
              )}
            </>
          ) : null}

          <Form.Check
            type="switch"
            id="sched-create-active"
            label="Active"
            checked={Boolean(form.is_active)}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            className="mb-3"
          />

          {isEdit && editRow?.next_run ? (
            <Form.Group className="mb-3">
              <Form.Label>Next run</Form.Label>
              <Form.Control
                plaintext
                readOnly
                value={new Date(editRow.next_run).toLocaleString()}
                className="text-muted"
              />
              <Form.Text className="text-muted">
                Advances after each execution; change schedule start below to reschedule while
                pending or failed.
              </Form.Text>
            </Form.Group>
          ) : null}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Schedule start <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.start_date}
                  onChange={(e) => onRepeatChange('start_date', e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  First run time; stays fixed while next run updates on repeat.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <SchedulerRepeatFields
            repeatFrequency={form.repeat_frequency}
            repeatInterval={form.repeat_interval}
            repeatOnDayKeys={form.repeat_on_day_keys}
            repeatAt={form.repeat_at}
            onChange={onRepeatChange}
            disabled={busy}
          />

          <SchedulerTimezoneSelect
            value={form.timezone}
            onChange={(tz) => setForm((f) => ({ ...f, timezone: tz }))}
            disabled={busy}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={busy}>
          {busy ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              {isEdit ? 'Saving…' : 'Creating…'}
            </>
          ) : isEdit ? (
            'Save'
          ) : (
            'Create'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
